export interface IndustryCopy {
  slug: string;
  idx: string;
  name: string;
  to: string;
  short: string;
  thesis: string;
  channels: string[];
  buyerNotes: string[];
  /** Optional meta description; overrides `short` in head tags only. */
  metaDescription?: string;
  /** Optional explicit <title>; overrides the generated one. */
  title?: string;
  /** Optional H1 override: "<h1Lead> <em>{h1Italic}</em>". */
  h1Lead?: string;
  h1Italic?: string;
  /** Long-form, industry-specific body copy rendered on the detail page. */
  deepDive?: { heading: string; paragraphs: string[] }[];
  channelEyebrow?: string;
  channelTitle?: string;
  buyerEyebrow?: string;
  buyerTitle?: string;
  crossLinksEyebrow?: string;
  crossLinksTitle?: string;
  resultsEyebrow?: string;
  resultsTitle?: string;
  faqEyebrow?: string;
  faqTitle?: string;
  /** Short research-evidence paragraph rendered under the thesis. Supports [label](/path) links. */
  evidence?: string;
  /** Hide the outcomes band (trades where we have no client work to point to). */
  hideResults?: boolean;
}


/**
 * Detail routes that have full thesis / channel / buyer-note copy.
 * These are linkable; the rest of the verticals appear in EXPANDED_INDUSTRIES
 * as editorial entries on the /industries overview.
 */
export const INDUSTRIES: IndustryCopy[] = [
  {
    slug: "roofing",
    evidence:
      "Of 297 roofing sites we loaded, 219 (73.7%) had no conversion tracking; 21 of the 62 confirmed advertisers measured nothing. Method: our [conversion tracking report](/research/conversion-tracking-report).",
    title: "Local Services Ads & Google Ads for Roofing Companies",
    metaDescription:
      "Local Services Ads, Google Ads and Meta for roofing companies: storm response, retail re-roofs and inspection leads, with spend that moves with the weather.",
    h1Lead: "Local Services Ads and Google Ads for",
    h1Italic: "roofing companies.",
    idx: "01",
    name: "Roofing",
    to: "/industries/roofing",
    short:
      "Storm response, retail re-roofs, and inspection lead engines. Seasonality and storm cycles drive the calendar.",
    thesis:
      "Roofing is two businesses in one channel mix: steady retail re-roofs and storm response that spikes. Paid media must handle both without overspending when the radar is quiet.",
    channels: ["Google Search", "Performance Max", "Local Services Ads", "Meta Ads"],
    buyerNotes: [
      "Storm-trigger ramp-up playbooks for hail and wind events",
      "Geo-segmented campaigns by insurance vs. retail intent",
      "LSA verification and lead review for high-ticket roofing leads",
      "Creative tested against inspection offers, not generic brand spots",
    ],
    deepDive: [
      {
        heading: "Local Services Ads for roofers",
        paragraphs: [
          "For roofers, [Local Services Ads sit above the search results](/services/local-services-ads-for-roofers), the first thing a homeowner sees after a storm.",
          "LSAs now run inside the same Google Ads account as Search, paced against one combined budget. A run of expensive LSA leads on a Monday hits Search the same week, so we plan and read both as one budget.",
          "Our full approach is on the [Local Services Ads service page](/services/local-services-ads); the roofing playbook is on [Local Services Ads management for roofers](/services/local-services-ads-for-roofers).",
          "First the account must clear Google Verified screening: a business-level background check, a current state roofing license in the legal entity's name, and general liability insurance at Google's limits, the certificate naming the same entity.",
          "Roofing onboardings commonly stall here, on a license held personally rather than by the LLC or a certificate that expires mid-review. Renewals matter too: an expired certificate suspends the badge, and a suspended badge stops the leads.",
          "LSA charges per lead and counts contacts a roofer would not: wrong numbers, tire-kickers outside the service area, solar and gutter inquiries, calls that disconnect in four seconds.",
          "Google credits low-quality charged leads automatically, usually within 30 days, but no longer credits leads marked job type or geo not serviced. So every week we keep job types and service area accurate and rate bad leads with Rate this lead.",
          "The older process is in [our earlier guide to LSA disputes](/blog/mastering-lsa-disputes-recover-20-of-monthly-ad-spend).",
          "LSA reporting is incomplete by construction: it shows charged leads, not credited ones, and uncharged contacts never line up with the rest of the account. Until it is reconciled against credits and the CRM, any blended cost per job including LSA is wrong.",
          "Storms rewrite the calendar. [Retail re-roof demand](/blog/how-to-structure-roofing-google-ads-and-lsa-for-retail-leads) is steady enough to budget monthly; storm demand arrives in days and vanishes as fast.",
          "The day after a hail event, quiet-month bids are wrong: every metro roofer bids on the same zip codes, and the leads are still worth taking. That call takes hours, as in [scaling Meta and LSAs in 48 hours after a storm](/blog/roofing-storm-response-ads-scaling-meta-lsas-in-48-hours).",
          "If you are weighing bought leads against ads, read [roofing leads: buying them vs LSA vs Google Ads](/blog/roofing-leads-buy-vs-lsa-vs-google-ads).",
        ],
      },
    ],
  },
  {
    slug: "painting",
    evidence:
      "Of 233 painting company websites we loaded, 190, 81.5%, had no conversion tracking, one of the worst trades in our [conversion tracking report](/research/conversion-tracking-report).",
    title: "Google Ads & Meta Ads for Painting Contractors",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "painting contractors.",
    idx: "02",
    name: "Painting",
    to: "/industries/painting",
    short:
      "Interior and exterior residential painting with a seasonal exterior peak. Margins reward lead quality over raw volume.",
    thesis:
      "Painting margins live on lead quality: job size, neighborhood and prep-vs-paint mix outweigh form volume, so we tune for qualified inquiries.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads", "YouTube"],
    buyerNotes: [
      "Seasonal exterior ramp from spring through early fall",
      "Neighborhood-level geo targeting around past sold jobs",
      "Meta creative tested against finish quality, not price",
      "Booked-job event optimization, not raw form fills",
    ],
  },
  {
    slug: "pool-service",
    evidence:
      "Of 284 pool service websites we loaded, 152 (53.5%) had no conversion tracking, the best of any trade we scanned: see our [conversion tracking report](/research/conversion-tracking-report).",
    title: "Google Ads & Meta Ads for Pool Service Companies",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "pool service companies.",
    idx: "03",
    name: "Pool Service",
    to: "/industries/pool-service",
    short:
      "Recurring service plans, repairs, and seasonal openings/closings. Lifetime value justifies aggressive acquisition.",
    thesis:
      "Pool service is a subscription business dressed as a trade: recurring chemistry and maintenance plans justify higher acquisition costs, if attribution can see them.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads", "Performance Max"],
    buyerNotes: [
      "Seasonal opening / closing demand spikes",
      "LTV-based bid signals for recurring plan customers",
      "Geographic radii built around route density, not city lines",
      "Bilingual creative for US markets where warranted",
    ],
    deepDive: [
      {
        heading: "Google Ads for pool cleaning and maintenance",
        paragraphs: [
          "Pool cleaning and maintenance plans run in separate campaigns from one-off repairs, each bid on its own economics and reported separately, because one ad account is pricing two very different things.",
          "A plan customer pays monthly for years; a repair is one invoice. Repairs cost less to acquire, so a shared bid drifts toward them and away from the plans that carry the business.",
          "The creative split is in [Meta ad strategies for pool cleaning versus repair leads](/blog/meta-ad-strategies-for-pool-cleaning-vs-repair-leads).",
          "A plan customer is [worth the sum of the months they stay](/blog/how-to-scale-pool-service-meta-ads-for-recurring-revenue), so a plan lead justifies a multiple of one clean. The operator sets that from their own retention numbers, not an agency guess.",
          "It goes into the account as an explicit conversion value, not a rule of thumb, so automated bidding prefers the lead worth more over the one that closes fastest.",
          "Budget follows the market's demand curve. Openings cluster in spring and closings in autumn where needed; in the Phoenix metro it flattens into a long chemistry-and-maintenance season with heat-driven repair spikes. A flat budget underspends while plan customers choose and overspends in dead weeks.",
          "Route density decides profit. A pool twenty minutes past the last stop on a route costs the tech drive time every visit for the life of the contract; one two streets from four current customers is nearly free.",
          "So draw campaign geography around existing routes, not city lines, and redraw as they fill.",
          "So measurement looks past the first job: plan sign-ups rather than form fills, plan-versus-repair mix by campaign, and retention by channel, which needs the CRM and ad account to agree on source. Franchise and multi-territory operators need [tracking pool franchise conversions across multiple territories](/blog/tracking-pool-franchise-conversions-across-multiple-territories).",
        ],
      },
    ],
  },
  {
    slug: "landscaping",
    evidence:
      "Of 239 landscaping websites we loaded, 184 (77.0%) had no conversion tracking. Method: our [conversion tracking report](/research/conversion-tracking-report).",
    title: "Google Ads & Meta Ads for Landscaping Companies",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "landscaping companies.",
    idx: "04",
    name: "Landscaping",
    to: "/industries/landscaping",
    short:
      "Paid media for landscape design-build, recurring maintenance, and installation demand, structured around project fit, seasonality, and crew capacity.",
    thesis:
      "A landscape architect selling six-month transformations and a weekly maintenance crew sell to different buyers, so they get separate budgets and definitions of success.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads", "YouTube"],
    buyerNotes: [
      "Design-build consultations separated from maintenance estimates",
      "Phoenix heat and planting calendars reflected in budget pacing",
      "Service areas drawn around crew starts, disposal runs, and route density",
      "Project scope captured before a lead reaches the estimator",
    ],
    channelEyebrow: "Demand by service line",
    channelTitle: "Where landscape buyers begin.",
    buyerEyebrow: "Estimate quality",
    buyerTitle: "Signals worth sending to the crew.",
    crossLinksEyebrow: "Campaign tools",
    crossLinksTitle: "Channels for landscape demand.",
    resultsEyebrow: "Measurement",
    resultsTitle: "From inquiry to scheduled consultation.",
    faqEyebrow: "Landscaping search questions",
    faqTitle: "What landscape operators ask.",
    deepDive: [
      {
        heading: "Design-build and maintenance belong in different accounts of value",
        paragraphs: [
          "Design-build and maintenance are different decisions, so they run as separate campaigns. Landscape [design-build starts with taste, trust](/blog/scaling-design-build-landscaper-meta-ads-cac-creative-strategy), property fit and a willingness to enter a consultative process.",
          "Maintenance starts with route coverage, frequency, scope and execution. Mix the two and the easier form fill absorbs [budget intended for larger projects](/blog/scaling-high-margin-landscaping-design-build-via-paid-media), hiding which side created the inquiry.",
          "The split starts before bidding. [Design-build keywords](/blog/how-to-get-design-build-leads-without-bidding-wars), ads, imagery, pages, forms and conversion actions describe a design conversation. Maintenance states cadence, property type and area. Installation-only requests get their own language, so irrigation repair is never treated as a design consultation.",
        ],
      },
      {
        heading: "A booked consultation says more than a landscaping form fill",
        paragraphs: [
          "A scheduled consultation tells bidding more than a form fill: the prospect accepted a time and entered the sales process.",
          "A short form can come from someone wanting tree trimming, weekly mowing, a full redesign or work outside the territory; bidding sees them all as identical.",
          "Calls need the same discipline: the office records whether the call fit a service sold, a workable address and a scheduled estimate. Importing that gives [Google Ads management](/services/google-ads) a signal tied to sales capacity, not to people who tap and hang up.",
        ],
      },
      {
        heading: "Phoenix landscaping demand follows heat, water, and planting decisions",
        paragraphs: [
          "In the Phoenix metro, landscape demand follows extreme heat, irrigation stress, monsoon damage, pool-adjacent outdoor living and planting windows unlike a national spring-and-fall calendar.",
          "Desert conversion, turf replacement, irrigation repair and patio shade are different searches, and national templates flatten them into broad seasonal assumptions.",
        ],
      },
      {
        heading: "Low-margin repair searches need an explicit boundary",
        paragraphs: [
          "Set the service boundary explicitly, or you pay to discover it one call at a time. Broad landscaping terms pull work that may not fit the crew: sprinkler heads, single-valve repairs, haul-away, tree work, pest questions, material shoppers, DIY research.",
          "Whether those belong depends on the service menu. Build the boundary with service-specific keywords, negative keywords, ad copy naming the accepted scope, and easy qualification questions.",
          "Exclude phrases that keep producing declined work in office dispositions. If a repair is accepted only with a maintenance agreement or install, say so before the click.",
        ],
      },
      {
        heading: "Crew geography is the hidden bidding constraint",
        paragraphs: [
          "Travel, loading, disposal and the day a crew can reach the property decide what a landscape lead is worth, and city boundaries are poor proxies for that.",
          "A compact maintenance route can support addresses unattractive for design-build, while a good installation can justify travel a weekly mowing stop cannot. So maintenance areas follow route clusters and tighten as days fill; design-build territory follows estimator availability and project fit.",
          "Target by physical presence, not interest in the location, so searches from outside the territory do not spend the budget.",
          "Before raising spend, the [free conversion tracking audit](/audit) shows whether the site records a real form completion and whether ad tags are visible: the trustworthy event that consultation and sold-work data connect to later.",
        ],
      },
    ],
  },
  {
    slug: "general-contractors",
    title: "Google Ads & Meta Ads for General Contractors",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "general contractors.",
    idx: "05",
    name: "General Contractors",
    to: "/industries/general-contractors",
    short:
      "Remodels, additions, and full builds. Long sales cycles and high ticket sizes: attribution is mandatory.",
    thesis:
      "GC sales cycles run weeks to months. Without offline conversion tracking, bidding chases the fastest form fills, not closed jobs, so we rebuild the loop.",
    channels: ["Google Search", "Performance Max", "Meta Ads", "YouTube"],
    buyerNotes: [
      "CRM-integrated offline conversion tracking",
      "Project-type segmented campaigns (remodel vs. addition vs. new build)",
      "Long-window retargeting tuned to actual decision timelines",
      "Showcase creative: portfolio first, offer second",
    ],
  },
  {
    slug: "hardscape-drywall",
    title: "Google Ads & Meta Ads for Hardscape & Drywall Contractors",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "hardscape and drywall contractors.",
    idx: "06",
    name: "Hardscape & Drywall",
    to: "/industries/hardscape-drywall",
    short:
      "Patios, retaining walls, drywall repair and finish. Project sizes vary widely: bid strategy has to keep up.",
    thesis:
      "Hardscape and drywall jobs range from hundreds to tens of thousands of dollars, so we bid by project value band.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads"],
    buyerNotes: [
      "Project-value bid segmentation",
      "Service-line landing pages with clear scope and ballpark pricing",
      "LSA lead review and accurate job types for under-spec leads",
      "Geo-tight campaigns focused on route efficiency",
    ],
  },
  {
    slug: "home-services",
    title: "Google Ads for Home Services",
    idx: "07",
    name: "Home services",
    to: "/industries/home-services",
    h1Lead: "Google Ads for",
    h1Italic: "home services.",
    short:
      "HVAC, plumbing, electrical and adjacent trades. Emergency demand, maintenance plans, and replacement jobs all behave differently in an ad account.",
    thesis:
      "Home services demand is urgent: the homeowner calls the first credible company that answers. Search and Local Services Ads are the core, and answering fast beats bid changes.",
    channels: ["Google Search", "Local Services Ads", "Performance Max", "Meta Ads"],
    buyerNotes: [
      "Emergency vs. replacement campaign separation with different bid ceilings",
      "Call tracking with duration thresholds so misdials stop counting as leads",
      "After-hours and weekend bid schedules matched to who actually answers",
      "Maintenance-plan enrollment tracked as its own conversion action",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "Home services accounts hold three buyers with different intent, and they should never share a campaign.",
          "The emergency buyer has no furnace at 6am in January, calls three companies in four minutes and books whoever answers. The maintenance buyer converts on a tune-up coupon. The replacement buyer spends five figures after two weeks, three quotes and a financing conversation.",
          "Blended, automated bidding chases the cheapest conversion, usually the tune-up, and defunds the replacement keywords that carry the margin. We split by intent first, then let bidding optimize inside each bucket.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "Local Services Ads sit on top and charge per lead: the first spend for licensed trades. Search covers what LSA cannot target: brand names, system types, financing.",
          "Performance Max waits for clean conversion data, and [Meta is for maintenance plans](/blog/scaling-home-services-meta-ads-cutting-cpl-from-86-to-34) and replacement offers, not emergencies. Nobody scrolls Instagram looking for a plumber.",
          "Lead cost swings by trade and market: tune-up and drain-clearing queries in the tens of dollars, competitive HVAC replacement and commercial plumbing in the low hundreds in metro markets. One number for 'home services' ignores your service mix.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The most common failure is counting. Phone clicks, form loads and chat opens all register as conversions, so the account reports two or three times the real leads and bidding chases the easiest fake one.",
          "The second is answer rate. Accounts where nobody answers after 5pm buy leads for competitors, so we check answer rate and call duration before touching budget.",
        ],
      },
    ],
  },
  {
    slug: "tree-services",
    evidence:
      "We loaded 160 tree service websites: 120, 75%, had no conversion tracking. The method matches our [conversion tracking report](/research/conversion-tracking-report), but this figure was read on 6 October 2026 and is not in its published table.",
    title: "Google Ads for Tree Services | Removal, Trimming & Storms",
    metaDescription:
      "Google Ads and Local Services Ads for tree service companies. Removal, trimming and storm emergency response, with spend that moves with the weather.",
    idx: "08",
    name: "Tree services",
    to: "/industries/tree-services",
    h1Lead: "Google Ads for",
    h1Italic: "tree services.",
    short:
      "Removal, trimming, and storm emergency response. Weather creates the demand, and spend has to move with it.",
    thesis:
      "Tree work is weather-driven and local. Spend has to expand within hours of a storm, contract as fast, and stay within reach of the crew and equipment.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads"],
    buyerNotes: [
      "Storm-triggered budget escalation by affected zip code",
      "Removal vs. trimming vs. stump grinding separated by job value",
      "Radius set by equipment transport time, not city boundaries",
      "Emergency keywords bid separately from planned maintenance work",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "Two demand curves overlap, planned and emergency, with an enormous gap in willingness to pay.",
          "Planned work (trimming, thinning, seasonal cleanup) is compared across two or three estimates and scheduled weeks out. Emergency work is a limb through a roof at midnight, booked with whoever can be there in the morning.",
          "Emergencies arrive as calls, planned work as forms. One shared conversion action cannot tell a $400 trim from a $6,000 crane removal, so bidding drifts toward the cheapest lead.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "Search is the backbone, because tree work is almost entirely search-triggered.",
          "Local Services Ads add a verified badge, which matters where homeowners are wary of uninsured operators. Meta plays a smaller role: seasonal planned work and neighborhood retargeting after a storm.",
          "Cost per lead varies more by season than keyword. The 48 hours after a wind event are the year's most competitive auction, and emergency removal leads cost several times a trim, which is fine: the job is worth several times as much.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The classic mistake is a flat budget: the same daily cap in February and after a derecho underspends when demand flows and overspends when nobody has a damaged tree.",
          "The second is over-broad geography. A job forty minutes outside the service area can lose money even at a good price, so we set radii by transport reality and exclude the fringe.",
        ],
      },
    ],
  },
  {
    slug: "cleaning",
    evidence:
      "Of 165 cleaning websites we loaded, 124 (75.2%) had no conversion tracking. Method: our [conversion tracking report](/research/conversion-tracking-report).",
    idx: "09",
    name: "Cleaning",
    to: "/industries/cleaning",
    h1Lead: "Google Ads for",
    h1Italic: "cleaning companies.",
    title: "Google Ads for Cleaning Companies",
    short:
      "Google Ads for recurring residential cleaning, deep cleans, and commercial contracts, measured by retention, phone handling, and route fit.",
    thesis:
      "Recurring residential, one-off deep cleaning and commercial janitorial work have different cash flows, so the bid reflects the relationship sold, not the first visit's price.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads"],
    buyerNotes: [
      "Recurring-plan enrollment kept separate from one-time bookings",
      "Phone and form leads reconciled with scheduled and retained service",
      "Commercial walkthroughs separated from residential availability checks",
      "Service areas organized around crew handoffs and repeat routes",
    ],
    channelEyebrow: "Booking paths",
    channelTitle: "Where cleaning customers commit.",
    buyerEyebrow: "Retention signals",
    buyerTitle: "What the first clean cannot tell you.",
    crossLinksEyebrow: "Acquisition mix",
    crossLinksTitle: "Channels for recurring and contract work.",
    resultsEyebrow: "Account feedback",
    resultsTitle: "From call to retained cleaning plan.",
    faqEyebrow: "Cleaning advertising questions",
    faqTitle: "What cleaning operators need answered.",
    deepDive: [
      {
        heading: "Recurring plans and deep cleans carry different value",
        paragraphs: [
          "A recurring plan is a continuing relationship; a move-out, post-construction or seasonal deep clean is a project with an end. One conversion value for both lets automated bidding favor the easiest booking instead of the customer that fits.",
          "The operator sets the recurring acquisition limit from their own retention, labor, supplies, travel and cancellation data. A plan contributes across future visits; a one-off job carries its acquisition cost alone. No outside benchmark replaces those numbers, because crew pay, scope and route density vary.",
        ],
      },
      {
        heading: "The phone and the form reveal different cleaning intent",
        paragraphs: [
          "Phone and form leads signal different intent, and neither is better. A caller may be standing in a property that needs service now; a form visitor may want a written quote or be writing after hours.",
          "What matters is tying each inquiry to a serviceable address, scope, appointment and plan status. Call tracking counts a real conversation reaching a qualifying point, not every tap or ring.",
          "Forms complete on the company's own confirmation state and carry the service requested, with personal details kept out of analytics; the CRM later returns a status (scheduled, completed, recurring, not serviceable).",
        ],
      },
      {
        heading: "Residential and commercial cleaning do not share a sales clock",
        paragraphs: [
          "A commercial inquiry starts a different sales process, not a delayed residential booking. Residential buyers decide on a home, a date, a frequency and trust; commercial buyers involve facilities staff, procurement, insurance, building access, walkthroughs, scope documents and several approvers.",
          "So keywords and pages split. House-cleaning ads lead to a residential availability and estimate flow; janitorial and office-cleaning ads lead to a commercial page asking facility type, schedule, square footage range, access and walkthrough needs.",
          "Mixed, residential terms consume commercial budget.",
        ],
      },
      {
        heading: "Lifetime value should set the bid ceiling",
        paragraphs: [
          "The price of an initial clean is [a poor ceiling for recurring acquisition](/blog/recurring-vs-deep-clean-ads-ltv-bidding-for-cleaning-companies) because it ignores future service. Assuming every booking stays forever is the opposite mistake.",
          "A defensible value uses the company's average relationship, margin after labor and supplies, cancellations, re-clean cost, discounting and travel. If a retained plan earns more margin than a one-off clean, the plan event gets a proportionally higher value: a framework, not a benchmark.",
        ],
      },
      {
        heading: "Route fit belongs in the lead definition",
        paragraphs: [
          "A lead inside the advertised city can still be poor if it sits past the edge of a crew's day; capacity runs on people, vehicles, keys and repeat windows, which radius targeting cannot see.",
          "A [conversion tracking audit](/audit) verifies that forms and core ad tags record. Then [Local Services Ads management](/services/local-services-ads) and Search are compared on charged leads, valid contacts, scheduled cleans and retained plans, not platform totals.",
        ],
      },
    ],
  },
  {
    slug: "construction",
    idx: "10",
    name: "Construction",
    to: "/industries/construction",
    h1Lead: "Google Ads for",
    h1Italic: "construction firms.",
    short:
      "Commercial and specialty construction. Few leads, very large contracts, and a decision cycle that outlives the default attribution window.",
    thesis:
      "Construction paid search produces few, very valuable inquiries, and the contract is signed months after the click, long after the ad platform stopped taking credit.",
    channels: ["Google Search", "Performance Max", "LinkedIn-adjacent Meta targeting", "YouTube"],
    buyerNotes: [
      "Offline conversion imports from the CRM so bids learn from signed work",
      "Project-type segmentation: tenant improvement, ground-up, specialty trade",
      "Qualification fields on the inquiry form to filter unbuildable requests",
      "Long retargeting windows matched to real procurement timelines",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "A commercial construction inquiry starts a procurement process, not a purchase.",
          "A developer or facilities director shortlists firms, invites bids, negotiates scope and signs two months to a year later. Several people decide, and none fills in a form twice.",
          "The platform scores every form fill the same, unaware that one was a $4M tenant improvement and forty were homeowners who misread the ad.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "Search carries the qualified demand: trade terms, project types and geography. Performance Max helps only once the conversion definition is trustworthy. Video and paid social are credibility surfaces (completed project walkthroughs for a narrow professional audience), not direct response.",
          "Cost per inquiry often runs well into the hundreds of dollars, and that is correct. What matters is cost per qualified bid opportunity: one contract a quarter from paid search can put a firm far ahead.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The first failure is judging on a 30-day window. Spend gets cut in month two, just before month one's inquiries turn into bids.",
          "The second is unfiltered inquiry forms. Without qualification fields (project type, budget range, timeline, location), residential requests drown the sales team. Fewer, harder-qualified inquiries work better, with closed ones fed back into bidding.",
        ],
      },
    ],
  },
  {
    slug: "business-services",
    title: "Google Ads for Business Services",
    idx: "11",
    name: "Business services",
    to: "/industries/business-services",
    h1Lead: "Google Ads for",
    h1Italic: "business services.",
    short:
      "B2B services with a defined buyer. Fewer searches, higher intent, and a pipeline that has to be reported by stage rather than by form fill.",
    thesis:
      "B2B search is small and expensive, so precision beats reach: buy the searches that match your ideal client, then prove with pipeline data which became revenue.",
    channels: ["Google Search", "Meta Ads", "YouTube", "Remarketing"],
    buyerNotes: [
      "Tight keyword sets around problem-aware and vendor-comparison queries",
      "CRM stage imports so bidding optimizes to qualified pipeline, not MQLs",
      "Firmographic exclusions and negative lists to keep out consumer traffic",
      "Content and demo offers separated into distinct conversion actions",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "A B2B services buyer identifies a problem, researches approaches, builds a shortlist, brings in more stakeholders, then buys.",
          "Paid search meets that path at problem research and vendor comparison, and each moment needs its own ads and landing pages. Few buyers convert on the first visit, so remarketing is structural, not an afterthought.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "Search volume is thin: a few hundred relevant searches a month is normal for a specialized service. Paid social and video build awareness before anyone searches. Neither replaces the other.",
          "Three-figure B2B lead costs are routine, and one competitive professional click can cost more than a consumer lead. That only works with a known win rate and contract value, so CRM stage reporting comes before scaling budget.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The first failure is optimizing to gated content downloads: whitepaper fills are cheap, abundant and never pipeline, so the account drifts away from buyers.",
          "The second is broad match without negatives. Business service terms overlap with job seekers, students and consumers, and unmanaged accounts spend a meaningful share on people who will never be clients.",
        ],
      },
    ],
  },
  {
    slug: "consumer-goods",
    title: "Meta and Google Ads for Consumer Goods",
    idx: "12",
    name: "Consumer goods",
    to: "/industries/consumer-goods",
    h1Lead: "Meta and Google Ads for",
    h1Italic: "consumer goods.",
    short:
      "Paid media for consumer brands where creative, contribution margin, product feeds, returns, and first-party purchase data govern scale.",
    thesis:
      "Creative and contribution margin, not audience size, limit consumer-goods ads. They need trusted purchase data and economics net of fulfillment and returns.",
    channels: ["Meta Ads", "Google Shopping", "Google Search", "YouTube"],
    buyerNotes: [
      "Creative concepts organized by customer problem and product proof",
      "Purchase events deduplicated across browser and server sources",
      "Shopping feeds maintained as product data, not treated as ad copy",
      "Bid targets based on contribution after fulfillment and returns",
    ],
    channelEyebrow: "Demand creation and capture",
    channelTitle: "Where a product earns attention.",
    buyerEyebrow: "Commerce constraints",
    buyerTitle: "Signals behind a viable order.",
    crossLinksEyebrow: "Commerce media",
    crossLinksTitle: "Channels for product discovery and capture.",
    resultsEyebrow: "Commercial measurement",
    resultsTitle: "From attributed purchase to contribution margin.",
    faqEyebrow: "Consumer brand questions",
    faqTitle: "What commerce operators need to know.",
    deepDive: [
      {
        heading: "On Meta, the creative does the targeting",
        paragraphs: [
          "On Meta, the creative tells the delivery system who to reach: the image, opening frame, demonstration, person, setting, problem and promise.",
          "As interest targeting loosens and [campaign automation has taken over](/blog/is-meta-advantage-shopping-better-than-manual-ads) audience choices, creative is the clearest input a brand controls. A new audience for an exhausted concept adds no reason to buy.",
          "Separate concepts from executions. A concept shows the problem, demonstrates the product, compares an alternative, answers an objection or fits the item into a routine. Executions vary hook, format, speaker, edit and placement, showing whether an idea failed or one version did.",
        ],
      },
      {
        heading: "Browser loss changes what the bidding system can learn",
        paragraphs: [
          "Browser pixels no longer see every purchase. Consent choices, browser restrictions, device changes, delayed purchases and checkout environments break the event stream, so bidding gets fewer confirmed outcomes and channel reports disagree more often.",
          "[Server-side purchase events](/blog/meta-ads-capi-and-offline-events-data-driven-roas-growth) restore part of the signal when they share an event identifier with the browser event and are deduplicated. Without that, one order arrives twice and can [inflate the platform's purchase count](/blog/why-does-meta-report-more-purchases-than-my-store). Poor matching fields leave server events unconnected to the ad.",
        ],
      },
      {
        heading: "Contribution margin and returns define the real CPA ceiling",
        paragraphs: [
          "Revenue is not your acquisition budget. Product cost, pick-and-pack, payment fees, shipping subsidies, discounts, returns, damaged inventory and customer service sit between sales and contribution, so a strong return on ad spend can still buy unprofitable orders.",
          "Contribution after product, fulfillment, fees, discounts and expected returns sets the maximum acquisition cost for a first order. Count repeat purchase only from the brand's own cohort behavior, discounted for uncertainty, never an industry benchmark.",
        ],
      },
      {
        heading: "Shopping captures intent that the product feed can explain",
        paragraphs: [
          "Google Shopping begins with the feed, not ad copy. Titles, product types, identifiers, variants, availability, price, shipping, images and landing-page consistency decide which searches a product enters. Bid changes cannot repair a vague feed or wrongly mapped variants.",
          "Search-term review, product-level reporting and brand exclusions [separate capture from creation](/blog/how-to-allocate-dtc-media-spend-between-meta-and-google-ads): which spend introduced the product, which answered active demand, and whether the combined acquisition cost fits the margin.",
        ],
      },
      {
        heading: "First-party data is the durable audience input",
        paragraphs: [
          "First-party data stays useful as third-party signals weaken. Purchasers, repeat purchasers, high-return customers, subscribers, lapsed buyers, wholesale accounts and product-family interests belong in separate lists, because each tells the platforms who to reach or suppress.",
          "List quality beats list size: collect with consent, normalize before upload, and segment by distinctions that change a decision. Exclude purchasers from first-order offers; reach them through replenishment or cross-sell. Never feed high-return or canceled orders to bidding as ideal customers.",
          "The list supplies an observed relationship; the ad supplies the reason a similar person might care now. The account holds up when product economics, feed quality, creative testing and purchase data agree on what a valuable order is.",
        ],
      },
    ],
  },
  {
    slug: "food-beverage",
    title: "Paid Media for Food & Beverage",
    idx: "13",
    name: "Food & beverage",
    to: "/industries/food-beverage",
    h1Lead: "Paid media for",
    h1Italic: "food & beverage.",
    short:
      "Multi-location restaurants and consumer F&B brands. Local intent capture and brand-side demand belong in one plan but not one campaign.",
    thesis:
      "Food and beverage marketing serves two ranges: the diner choosing where to eat in thirty minutes, and the shopper remembering your label in an aisle next week. Same budget, different measurement.",
    channels: ["Google Search", "Meta Ads", "Performance Max", "YouTube"],
    buyerNotes: [
      "Location-level campaigns with radius targeting per venue",
      "Order and reservation platform conversions imported properly",
      "Retail-distribution brands measured on lift and branded search, not clicks",
      "Daypart scheduling matched to service hours and kitchen capacity",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "Restaurant demand is [immediate and hyper-local](/blog/scaling-store-visits-and-calls-with-google-local-campaigns): decided within an hour and a few miles, on proximity, photos and reviews. Ads work only if hours, menu and ordering links are correct everywhere.",
          "Packaged food and beverage brands are the opposite: the purchase happens days later in a grocery aisle or a bar, with no digital trail. Measure awareness by branded search volume, retail velocity and lift, not the conversions column.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "For venues, [Search and Maps capture people already choosing](/blog/local-f-b-paid-media-integrating-lsa-meta-and-google-ads), and Meta prompts the decision: a new menu, a patio opening, a weekend special inside a tight radius. Multi-location groups run one campaign per market, with budgets set by capacity.",
          "Local F&B clicks are cheap next to home services, so the discipline is attribution. An order that would have happened anyway is no win, so we separate branded from non-branded first.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The top failure is third-party ordering platforms swallowing the conversion: with nothing passed back, the account shows traffic and no results, and a working campaign gets cut.",
          "For brands, it is judging awareness on direct response: a distribution-led beverage brand measured on click-through purchases always looks like a failure.",
        ],
      },
    ],
  },
  {
    slug: "sports-fitness",
    title: "Paid Media for Sports & Fitness",
    idx: "14",
    name: "Sports & fitness",
    to: "/industries/sports-fitness",
    h1Lead: "Paid media for",
    h1Italic: "sports & fitness.",
    short:
      "Studios, gyms, leagues, and equipment brands. Membership economics mean the first month is never the whole story.",
    thesis:
      "Fitness acquisition is a retention business: the trial is not revenue, the eighth month is. Budget follows member tenure, and campaigns are judged on cohorts.",
    channels: ["Meta Ads", "Google Search", "Performance Max", "YouTube"],
    buyerNotes: [
      "Trial-to-paid conversion tracked as the real objective",
      "Cohort reporting by intake month, not blended lifetime averages",
      "Class and league capacity used as the ceiling on spend",
      "Seasonal intake peaks planned rather than reacted to",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "Fitness demand is intent-spiky and emotionally driven. January, post-holiday and pre-summer bring huge surges; the same ad in October buys a fraction of the signups. Even spending misses the cheap demand.",
          "The decision is short (see an offer, book a trial class or tour, attend within days), but the value is long. Members who stay six months are worth many times the trial-only crowd, and steep discounts attract people who leave with the discount.",
        ],
      },
      {
        heading: "Which platform fits, and what a lead costs",
        paragraphs: [
          "[Meta and Instagram do most of the work](/blog/how-do-fitness-studios-lower-meta-ads-cac-for-memberships) for studios and leagues, because the product is visual and social. Search catches deliberate seekers typing a discipline and a neighborhood: small but efficient. Equipment and apparel brands run like consumer goods.",
          "Trial costs are modest, often in the tens of dollars, but what matters is cost per retained member. If a third of trials convert and half stay past month three, real acquisition cost is several times the headline, and likely still profitable.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "The first failure is reporting signups as the win. A hundred free trials and eight retained members is a failed campaign, invisible until membership data is fed back.",
          "The second is ignoring capacity. Overfilled classes raise churn, which costs more than the ads saved. Cap spend by capacity, not ambition.",
        ],
      },
    ],
  },
  {
    slug: "marketing",
    idx: "15",
    name: "Marketing",
    to: "/industries/marketing",
    h1Lead: "White-label paid media for",
    h1Italic: "agencies.",
    short:
      "White-label Google and Meta management for agencies and consultancies. Discreet delivery, full reporting transparency, your client relationship.",
    thesis:
      "White-label delivery lets agencies sell paid media without hiring ahead of revenue or shipping mediocre work, if reporting is transparent enough to defend in front of your client.",
    channels: ["Google Search", "Performance Max", "Meta Ads", "Local Services Ads"],
    buyerNotes: [
      "Work performed inside your client's own ad accounts, never a reseller shell",
      "Reporting delivered unbranded and ready to present",
      "Named operator on the account rather than a rotating queue",
      "Month-to-month engagement, so your margin is never locked to ours",
    ],
    deepDive: [
      {
        heading: "How the buying cycle actually runs",
        paragraphs: [
          "Agencies buy when a client needs something they cannot serve: a retainer requiring Google Ads management, or a failing account that threatens the relationship. The clock is short, because the client is already asking.",
          "It is a trust exercise, not a sales cycle: who does the work, how fast issues escalate, and whether reporting arrives on time, ready to present.",
        ],
      },
      {
        heading: "How the engagement fits",
        paragraphs: [
          "We work inside your client's own Google Ads, Meta and analytics accounts. Nothing sits in a reseller container, conversion actions and history stay with the client, and access can be removed without anything switching off.",
          "You own the client relationship; we operate the account, unbranded. Reporting is built to be forwarded and separates platform-reported conversions from closed business, the gap where agency relationships usually break.",
        ],
      },
      {
        heading: "What goes wrong",
        paragraphs: [
          "First failure: opaque white-label vendors. Without account access, every client question waits two days and you take blame for what you could not inspect. Full account visibility is non-negotiable.",
          "The other is inherited measurement. Transferred accounts often carry duplicated conversion actions and inflated numbers, so we audit tracking first: no downward revision in your first report.",
        ],
      },
    ],
  },
  {
    slug: "electricians",
    title: "Google Ads for Electricians: Calls, Panels and EV Chargers",
    h1Lead: "Google Ads and LSA for",
    h1Italic: "electricians.",
    idx: "16",
    name: "Electricians",
    to: "/industries/electricians",
    short:
      "We loaded 326 electrician websites: 84.0% had no conversion tracking. How paid media works when most electrical leads arrive as a phone call.",
    thesis:
      "Electricians are the worst-measuring trade we scanned, and the gap sits where the work comes from: the phone call, the conversion most often left unrecorded.",
    channels: ["Google Search", "Local Services Ads", "Call tracking", "Performance Max"],
    buyerNotes: [
      "Emergency callouts separated from planned installs",
      "Calls recorded as conversions, with duration thresholds",
      "Panel upgrades and EV chargers bid on their own value",
      "After-hours schedules matched to who answers the phone",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How many electricians can actually see their leads?",
        paragraphs: [
          "Few. Of 326 electrician websites we loaded in a real browser, 274 (84.0%) had no conversion tracking, the highest rate of any trade in our [conversion tracking report](/research/conversion-tracking-report).",
          "Of the 50 we confirmed were running ads, 28 of them, 56.0%, had nothing measuring the result. Every bid, budget and keyword decision in those accounts is made on clicks alone.",
        ],
      },
      {
        heading: "Why do emergency and planned electrical work need separate campaigns?",
        paragraphs: [
          "In one campaign, bidding chases the cheaper emergency job and starves the high-ticket installs.",
          "An emergency callout (a dead circuit, a burning smell, a tripped main) goes to whoever answers first; a panel upgrade, EV charger install or whole-house rewire is quoted, compared and worth many times more.",
          "Split, each carries its own budget and bid ceiling in [Google Ads](/services/google-ads).",
        ],
      },
      {
        heading: "What does an untracked phone call cost an electrical business?",
        paragraphs: [
          "Most of what its ads produce: a site recording only forms sees a fraction, because electrical jobs usually start with a call. Call tracking with a minimum duration, so a two-second hang-up is not a lead, closes most of that gap.",
          "[Local Services Ads](/services/local-services-ads) report only charged calls, so reconcile them against the phone system.",
          "When a call-driven account suddenly reports nothing, suspect measurement: see [why Google Ads conversions stop reporting](/blog/why-did-my-google-ads-conversions-stop).",
        ],
      },
    ],
  },
  {
    slug: "plumbing",
    title: "Google Ads & Local Services Ads for Plumbers",
    h1Lead: "Google Ads and Local Services Ads for",
    h1Italic: "plumbers.",
    idx: "17",
    name: "Plumbing",
    to: "/industries/plumbing",
    short:
      "We loaded 251 plumbing websites: 72.9% had no conversion tracking. Why speed, call handling and Local Services Ads decide plumbing jobs.",
    thesis:
      "Plumbing demand is emergency-driven: someone with water on the floor calls whoever answers. Call handling decides the job, so measurement lives where the call happens.",
    channels: ["Local Services Ads", "Google Search", "Call tracking", "After-hours scheduling"],
    buyerNotes: [
      "Answer rate checked before any budget change",
      "After-hours calls tracked as their own segment",
      "Water heater and repipe work bid apart from drain calls",
      "LSA job types and ZIP codes set by margin",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How many plumbers are advertising without measurement?",
        paragraphs: [
          "Of the 55 plumbers we confirmed running ads, 23 of them, 41.8%, were advertising with no measurement.",
          "Of 251 plumbing websites we loaded, 183 (72.9%) had no conversion tracking: full figures in our [conversion tracking report](/research/conversion-tracking-report).",
          "So much plumbing revenue arrives by phone that untracked calls leave the account showing clicks, spend and little else.",
        ],
      },
      {
        heading: "Why do Local Services Ads matter so much for plumbing?",
        paragraphs: [
          "For a burst pipe, homeowners tap the first verified listing, so [Local Services Ads](/services/local-services-ads) carry a disproportionate share of plumbing demand: pay-per-lead, verified, on top.",
          "A water heater replacement and a drain clearing should not share a bid, and some ZIP codes cost more to serve than they return: see [setting LSA bids by service type and ZIP code](/blog/how-should-you-set-lsa-bids-by-service-type-and-zip-code).",
        ],
      },
      {
        heading: "What happens to after-hours plumbing calls?",
        paragraphs: [
          "Too often they reach voicemail or an answering service that never reports back, though they are a real share of plumbing revenue.",
          "No bid fixes an unanswered phone, so we check answer rate and call duration by hour before touching [Google Ads](/services/google-ads) budget, then schedule spend around answered hours.",
          "Overnight answering-service bookings must reach the ad account, or night campaigns look worthless while producing work.",
        ],
      },
    ],
  },
  {
    slug: "hvac",
    title: "Google Ads & LSA for HVAC Companies",
    h1Lead: "Google Ads and LSA for",
    h1Italic: "HVAC companies.",
    idx: "18",
    name: "HVAC",
    to: "/industries/hvac",
    short:
      "HVAC had more confirmed advertisers than any trade we scanned. 60.0% of 230 HVAC sites still had no conversion tracking. What that means for paid media.",
    thesis:
      "HVAC is the most advertising-mature trade we measured, with the most confirmed advertisers, yet six in ten sites had nothing, a gap seasonality makes expensive.",
    channels: ["Google Search", "Local Services Ads", "Performance Max", "Meta Ads"],
    buyerNotes: [
      "Repair, replacement and maintenance split by intent",
      "Heatwave and cold-snap budgets planned in advance",
      "Maintenance agreements valued by lifetime, not first visit",
      "Tune-up leads kept from defunding replacement keywords",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How well does HVAC measure compared with other trades?",
        paragraphs: [
          "Better than most, but not well. Of 230 HVAC websites we loaded, 138 (60.0%) had no conversion tracking, the second-lowest rate of the twelve trades in our [conversion tracking report](/research/conversion-tracking-report).",
          "HVAC also led every trade in confirmed advertisers: 68. Of those, 18, or 26.5%, had nothing measuring the result, so plenty of accounts are deciding on clicks.",
        ],
      },
      {
        heading: "Why does seasonality make a tracking gap more expensive?",
        paragraphs: [
          "HVAC demand is violently seasonal: a gap that is cheap in a mild April is expensive in a July heatwave, when every operator raises budgets and the account cannot tell which campaigns earned it.",
          "In [Google Ads](/services/google-ads) we plan peak budgets before the season. Automated campaigns need clean conversion data before scaling: see [structuring Performance Max for local home-service operators](/blog/structuring-google-pmax-for-local-home-service-operators).",
          "[Local Services Ads](/services/local-services-ads) sit alongside Search for no-heat and no-cool emergencies.",
        ],
      },
      {
        heading: "Why is lifetime value the number that matters in HVAC?",
        paragraphs: [
          "A maintenance agreement customer returns twice a year and is first in line for a replacement: no better than a repair on cost per lead, the most valuable lead on lifetime value.",
          "So agreement sign-ups and replacement sales return to the ad account as separate conversions with their own values. [Meta Ads](/services/meta-ads) suit pre-season maintenance plan offers, not emergencies.",
        ],
      },
    ],
  },
  {
    slug: "pest-control",
    title: "Google Ads & Meta Ads for Pest Control Companies",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "pest control companies.",
    idx: "19",
    name: "Pest control",
    to: "/industries/pest-control",
    short:
      "We loaded 213 pest control websites: 76.5% had no conversion tracking. Why cost per lead misprices a trade built on recurring contracts.",
    thesis:
      "Pest control sells subscriptions, not jobs. A one-off treatment and a recurring quarterly contract arrive through the same form, worth entirely different amounts.",
    channels: ["Google Search", "Local Services Ads", "Meta Ads", "Offline conversion imports"],
    buyerNotes: [
      "One-off treatments separated from recurring plans",
      "Contract values sent back to the ad platform",
      "Seasonal pest pressure planned by month and region",
      "Renewal and retention read by acquisition source",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How many pest control companies track their leads?",
        paragraphs: [
          "Most do not: 163 of the 213 pest control websites we loaded, 76.5%, had no conversion tracking of any kind.",
          "Of the 39 running ads, 16, 41.0%, advertised blind, per our [conversion tracking report](/research/conversion-tracking-report). Many accounts see no leads; the rest usually see only the first step.",
        ],
      },
      {
        heading: "Why does optimizing to cost per lead misprice pest control?",
        paragraphs: [
          "Cost per lead treats every inquiry as equal, and in pest control they are not.",
          "A wasp-nest treatment and a quarterly general pest contract cost about the same to acquire but differ greatly in yearly value, so cost-per-lead bidding buys more of the cheaper one.",
          "The fix: send values back once the office knows which customer it signed, as in [offline conversion imports versus form-fill bidding](/blog/google-ads-offline-conversion-imports-vs-form-fill-bidding). Same logic: [LTV bidding for cleaning companies](/blog/recurring-vs-deep-clean-ads-ltv-bidding-for-cleaning-companies).",
        ],
      },
      {
        heading: "Which channels fit a subscription service?",
        paragraphs: [
          "Search and [Local Services Ads](/services/local-services-ads) catch active infestations needing someone this week. [Google Ads](/services/google-ads) split by pest and plan type keeps recurring keywords on their own budget.",
          "[Meta Ads](/services/meta-ads) suit prevention: seasonal reminders and plan offers to homeowners without a problem yet.",
        ],
      },
    ],
  },
  {
    slug: "concrete",
    title: "Google Ads for Concrete Contractors",
    h1Lead: "Google Ads for",
    h1Italic: "concrete contractors.",
    idx: "20",
    name: "Concrete",
    to: "/industries/concrete",
    short:
      "We loaded 190 concrete contractor websites: 81.1% had no conversion tracking. Why residential and commercial inquiries need separating.",
    thesis:
      "Residential and commercial concrete are two businesses sharing one website, with different quote cycles and lead values most sites cannot separate.",
    channels: ["Google Search", "Meta Ads", "Offline conversion imports"],
    buyerNotes: [
      "Residential and commercial inquiries tracked separately",
      "Driveways, patios and slabs bid by project value",
      "Quote stage recorded, not only the first form",
      "Service radius set by pour logistics",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How many concrete contractors can measure their advertising?",
        paragraphs: [
          "Few: 154 of the 190 concrete contractor websites we loaded, 81.1%, had no conversion tracking, the third-worst rate of the twelve trades in our [conversion tracking report](/research/conversion-tracking-report).",
          "We publish no concrete advertiser rate. Only 20 businesses were confirmed advertising, too small a sample for a meaningful percentage.",
        ],
      },
      {
        heading: "Why do residential and commercial concrete need separate tracking?",
        paragraphs: [
          "A homeowner's driveway and a general contractor's commercial slab arrive through one form: one decided in days, the other bid for weeks and worth many times more.",
          "Separate forms or a required project-type field, separate conversion actions and separate [Google Ads](/services/google-ads) campaigns let each side be bid on its own economics.",
        ],
      },
      {
        heading: "Where do concrete leads go wrong after the click?",
        paragraphs: [
          "Much concrete inquiry is small work below the minimum job size or beyond a crew and truck's economic reach.",
          "Form qualification questions, negative keywords for work you skip, and a radius around pour logistics fix that, as in [improving hardscape lead quality in Google Ads](/blog/how-do-you-improve-hardscape-lead-quality-in-google-ads).",
        ],
      },
    ],
  },
  {
    slug: "remodeling",
    title: "Google Ads & Meta Ads for Remodeling Companies",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "remodeling companies.",
    idx: "21",
    name: "Remodeling",
    to: "/industries/remodeling",
    short:
      "Remodelers who advertise mostly do measure: 19.6% of advertisers had no tracking. The real gap is what happens after the inquiry.",
    thesis:
      "Remodelers who advertise mostly measure the inquiry. But the sale runs weeks to months through an in-home consultation, and form-only tracking never learns which leads became projects.",
    channels: ["Google Search", "Meta Ads", "YouTube", "Offline conversion imports"],
    buyerNotes: [
      "Consultation booked and project sold sent back as conversions",
      "Kitchen, bath and addition work segmented by value",
      "Long retargeting windows for long decisions",
      "Portfolio-led creative over discount offers",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "Do remodelers who advertise measure their results?",
        paragraphs: [
          "Mostly, yes: of the 46 remodelers we confirmed were running ads, only 9, 19.6%, were advertising blind, the second-best advertiser rate in our [conversion tracking report](/research/conversion-tracking-report).",
          "Across all 195 remodeling websites, 134, 68.7%, had no conversion tracking. The gap in this trade sits further down the funnel.",
        ],
      },
      {
        heading: "Why does tracking that stops at the form mislead a remodeling account?",
        paragraphs: [
          "A remodel is sold in a consultation, not on a website, often weeks or months after the form.",
          "Site visit, design, proposal, financing, signed project: inquiry-only tracking sees which leads arrived, never which became projects. Automated bidding then chases the most inquiries, often the smallest work.",
          "Sending consultation-booked and project-sold events back from the CRM corrects that, as in [offline conversion imports versus form-fill bidding](/blog/google-ads-offline-conversion-imports-vs-form-fill-bidding). Which jobs to drop is covered in [stop bidding for low-margin design-build jobs](/blog/design-build-lead-gen-stop-bidding-for-low-margin-jobs).",
        ],
      },
      {
        heading: "How do Search and Meta divide the work in remodeling?",
        paragraphs: [
          "[Google Ads](/services/google-ads) catches homeowners searching for a specific project, like a kitchen remodel or bathroom conversion, split by project type so higher-value work keeps its budget.",
          "[Meta Ads](/services/meta-ads) work earlier, showing finished projects in the right neighborhoods and retargeting across the long decision window.",
        ],
      },
    ],
  },
  {
    slug: "window-replacement",
    title: "Google Ads & Meta Ads for Window Replacement",
    h1Lead: "Google Ads and Meta Ads for",
    h1Italic: "window replacement companies.",
    idx: "22",
    name: "Window replacement",
    to: "/industries/window-replacement",
    short:
      "Window companies that advertise measure better than any trade we scanned: 16.7% of advertisers had no tracking. Where the remaining gap sits.",
    thesis:
      "Window companies that advertise measure better than any trade we scanned. The gap is the in-home estimate, the real conversion, and financing approval, which most tracking misses.",
    channels: ["Google Search", "Meta Ads", "Offline conversion imports", "Performance Max"],
    buyerNotes: [
      "In-home estimate set as the primary conversion",
      "Financing approvals imported as a separate stage",
      "Weather and season used to plan budget",
      "Replacement and repair inquiries kept apart",
    ],
    hideResults: true,
    deepDive: [
      {
        heading: "How well do window companies track their advertising?",
        paragraphs: [
          "Better than any other trade: of the 42 running ads, 7, 16.7%, advertised blind, the best rate of all twelve trades in our [conversion tracking report](/research/conversion-tracking-report).",
          "Across all 172 window replacement websites, 113, 65.7%, had no conversion tracking. The gap comes after the lead.",
        ],
      },
      {
        heading: "Why is the in-home estimate the real conversion?",
        paragraphs: [
          "A window replacement is sold in the home, at the estimate where price, product and financing are settled and the contract is signed.",
          "Counting only form fills treats a no-show like a signed whole-house job.",
          "Making the completed estimate the primary conversion, imported from the CRM, gives bidding a revenue signal. See [offline conversion imports versus form-fill bidding](/blog/google-ads-offline-conversion-imports-vs-form-fill-bidding), which applies directly in [Google Ads](/services/google-ads).",
        ],
      },
      {
        heading: "What does financing approval do to measurement?",
        paragraphs: [
          "It adds a second gate after the estimate that most tracking never sees, so campaigns producing approvals and campaigns producing declines look identical.",
          "Demand is also seasonal, peaking before winter and after storms. [Meta Ads](/services/meta-ads) build interest among homeowners in older housing stock ahead of those peaks; Search catches them when ready to book an estimate.",
        ],
      },
    ],
  },
];


export interface ExpandedIndustry {
  idx: string;
  name: string;
  descriptor: string;
  /** If present, this vertical has a dedicated detail page. */
  to?: string;
}

/**
 * Full vertical coverage shown on /industries and on the home Industries section.
 * Items with `to` link to detail pages; the rest are editorial entries - no
 * fabricated metrics, no fake routes, no "coming soon" badges.
 */
export const EXPANDED_INDUSTRIES: ExpandedIndustry[] = [
  {
    idx: "01",
    name: "Roofing",
    to: "/industries/roofing",
    descriptor:
      "Storm response and retail re-roofs. Inspection lead engines tuned to weather and insurance cycles.",
  },
  {
    idx: "02",
    name: "Painting",
    to: "/industries/painting",
    descriptor:
      "Interior and exterior residential painting. Neighborhood-level targeting, optimized for booked jobs not form fills.",
  },
  {
    idx: "03",
    name: "Pool services",
    to: "/industries/pool-service",
    descriptor:
      "Service plans, repairs, and seasonal openings. LTV-aware bidding for a subscription business dressed up as a trade.",
  },
  {
    idx: "04",
    name: "Landscaping",
    to: "/industries/landscaping",
    descriptor:
      "Design-build, maintenance, and installs. Account structure built so high-ticket work doesn't get cannibalized.",
  },
  {
    idx: "05",
    name: "General Contractors",
    to: "/industries/general-contractors",
    descriptor:
      "Remodels, additions, and new builds. Long sales cycles tracked end-to-end via offline conversion imports.",
  },
  {
    idx: "06",
    name: "Hardscape & Drywall",
    to: "/industries/hardscape-drywall",
    descriptor:
      "Patios, retaining walls, repair and finish. Bid strategy segmented by project value, not category average.",
  },
  {
    idx: "07",
    name: "Home services",
    to: "/industries/home-services",
    descriptor:
      "HVAC, plumbing, electrical, and adjacent trades. Lead-quality optimization across Search, LSA, and Meta.",
  },
  {
    idx: "08",
    name: "Tree services",
    to: "/industries/tree-services",
    descriptor:
      "Removal, trimming, and emergency response. Seasonal and weather-driven demand managed with elastic spend.",
  },
  {
    idx: "09",
    name: "Cleaning",
    to: "/industries/cleaning",
    descriptor:
      "Residential and commercial cleaning. Recurring-plan acquisition with route-density geo discipline.",
  },
  {
    idx: "10",
    name: "Construction",
    to: "/industries/construction",
    descriptor:
      "Commercial and specialty construction. Long-cycle attribution with project-type segmentation.",
  },
  {
    idx: "11",
    name: "Business services",
    to: "/industries/business-services",
    descriptor:
      "B2B services with defined ICPs. Lead-form quality, lifecycle nurturing, and pipeline-stage reporting.",
  },
  {
    idx: "12",
    name: "Consumer goods",
    to: "/industries/consumer-goods",
    descriptor:
      "DTC and retail-distribution brands. Creative-led Meta with Google branded-search lift measurement.",
  },
  {
    idx: "13",
    name: "Food & beverage",
    to: "/industries/food-beverage",
    descriptor:
      "Multi-location restaurants and consumer F&B brands. Local intent capture and brand-side awareness in one plan.",
  },
  {
    idx: "14",
    name: "Sports & fitness",
    to: "/industries/sports-fitness",
    descriptor:
      "Studios, leagues, equipment, and apparel. Member acquisition with cohort-based retention reporting.",
  },
  {
    idx: "15",
    name: "Marketing",
    to: "/industries/marketing",
    descriptor:
      "White-label paid media for agencies and consultancies. Discreet partnership, full reporting transparency.",
  },
  {
    idx: "16",
    name: "Electricians",
    to: "/industries/electricians",
    descriptor:
      "We loaded 326 electrician websites: 84.0% had no conversion tracking. How paid media works when most electrical leads arrive as a phone call.",
  },
  {
    idx: "17",
    name: "Plumbing",
    to: "/industries/plumbing",
    descriptor:
      "We loaded 251 plumbing websites: 72.9% had no conversion tracking. Why speed, call handling and Local Services Ads decide plumbing jobs.",
  },
  {
    idx: "18",
    name: "HVAC",
    to: "/industries/hvac",
    descriptor:
      "HVAC had more confirmed advertisers than any trade we scanned. 60.0% of 230 HVAC sites still had no conversion tracking. What that means for paid media.",
  },
  {
    idx: "19",
    name: "Pest control",
    to: "/industries/pest-control",
    descriptor:
      "We loaded 213 pest control websites: 76.5% had no conversion tracking. Why cost per lead misprices a trade built on recurring contracts.",
  },
  {
    idx: "20",
    name: "Concrete",
    to: "/industries/concrete",
    descriptor:
      "We loaded 190 concrete contractor websites: 81.1% had no conversion tracking. Why residential and commercial inquiries need separating.",
  },
  {
    idx: "21",
    name: "Remodeling",
    to: "/industries/remodeling",
    descriptor:
      "Remodelers who advertise mostly do measure: 19.6% of advertisers had no tracking. The real gap is what happens after the inquiry.",
  },
  {
    idx: "22",
    name: "Window replacement",
    to: "/industries/window-replacement",
    descriptor:
      "Window companies that advertise measure better than any trade we scanned: 16.7% of advertisers had no tracking. Where the remaining gap sits.",
  },
];
